import React, {useState, useEffect, useMemo} from 'react'
import {Box, Heading, useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useCustomFetch from '../../../../hooks/useFetchContent'
import Slider from 'react-slick'
import {useProduct} from '@salesforce/commerce-sdk-react'
import QuickView from '../../../product-list/partials/quick-view'
import {getUrlSearchParam} from '../../../../utills/url'
import {useLocation} from 'react-router-dom'
import {ProductsContainerStyles, ProductTileStyles} from './index.style'
import {CustomNextArrow, CustomPrevArrow} from '../../../../components/custom-arrows'

function ProductsCollection({category}) {
    const {token} = useAccessToken()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {response: productsResponse} = useCustomFetch(token, [
        `dynamiccategorylanding-products:category:${category?.id}`
    ])
    const [slides, setSlides] = useState([])
    const [productId, setProductId] = useState('')
    const [currentSlide, setCurrentSlide] = useState(0)
    const [sliderTitle, setSliderTitle] = useState('')
    const location = useLocation()

    const {data: product} = useProduct(
        {
            parameters: {
                id: productId,
                perPricebook: true,
                expand: [
                    'availability',
                    'promotions',
                    'options',
                    'images',
                    'prices',
                    'variations',
                    'set_products',
                    'bundled_products'
                ],
                allImages: true
            }
        },
        {
            enabled: !!productId
        }
    )

    useEffect(() => {
        if (productsResponse) {
            const parser = new DOMParser()
            const doc = parser.parseFromString(productsResponse, 'text/html')

            // Extract slider tiles
            const slideElements = doc.querySelectorAll('.product-tile')
            setSlides(Array.from(slideElements, (slide) => slide.outerHTML))

            // Extract slider title (h2)
            const headingEl = doc.querySelector('h2.landing-section__heading')
            if (headingEl) {
                setSliderTitle(headingEl.textContent.trim())
            }
        }
    }, [productsResponse])

    useEffect(() => {
        if (slides.length === 0) return

        const timeout = setTimeout(() => {
            const tiles = document.querySelectorAll('.product-tile')

            tiles.forEach((tile) => {
                const thumbLink = tile.querySelector('.thumb-link')
                const mainImg = tile.querySelector('.product-image img')
                const swatches = tile.querySelectorAll('.product-swatches .swatch img')

                if (!thumbLink || !mainImg) return

                // ---- 1. Extract product ID ----
                const href = thumbLink.getAttribute('href')
                const match = href?.match(/\/([A-Z0-9-]+)\.html$/i)
                const productId = match ? match[1] : null

                if (!productId) return

                // ---- 2. Create "Quick View" button ----
                const button = document.createElement('button')
                button.className = 'custom-action-button'
                button.textContent = 'Quick View'
                button.onclick = (e) => {
                    e.preventDefault()
                    setProductId(productId)
                    onOpen()
                }

                // ---- 3. Hover logic for showing button ----
                thumbLink.style.position = 'relative'
                thumbLink.addEventListener('mouseenter', () => {
                    button.style.bottom = '-35px'
                    button.style.opacity = '1'
                    button.style.pointerEvents = 'auto'
                })
                thumbLink.addEventListener('mouseleave', () => {
                    button.style.opacity = '0'
                    button.style.pointerEvents = 'none'
                })

                thumbLink.appendChild(button)

                // ---- 4. Swatch: Hover and Click change main image ----
                const originalSrc = mainImg.getAttribute('src')
                const isSmallScreen = window.innerWidth <= 768

                swatches.forEach((swatchImg) => {
                    const swatchLink = swatchImg.closest('a.swatch')
                    if (!swatchLink) return

                    const updateMainImage = () => {
                        const thumbData = swatchImg.getAttribute('data-thumb')
                        if (!thumbData) return

                        try {
                            const parsed = JSON.parse(thumbData.replace(/&quot;/g, '"'))
                            if (parsed.src) {
                                mainImg.src = parsed.src
                                mainImg.alt = parsed.alt || ''
                                mainImg.title = parsed.title || ''
                            }
                        } catch (err) {
                            console.error('Failed to parse swatch data-thumb', err)
                        }
                    }

                    const resetImage = () => {
                        if (originalSrc) {
                            mainImg.src = originalSrc
                        }
                    }

                    // Desktop: Hover to preview
                    swatchLink.addEventListener('mouseenter', updateMainImage)
                    swatchLink.addEventListener('mouseleave', resetImage)

                    // Mobile: Click to switch image permanently
                    if (isSmallScreen) {
                        swatchLink.addEventListener('click', (e) => {
                            e.preventDefault()
                            updateMainImage()
                        })
                    }
                })
            })
        }, 100)

        return () => clearTimeout(timeout)
    }, [slides, setProductId, onOpen])

    const settings = {
        arrows: true,
        infinite: false,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 4,
        autoplay: false,
        beforeChange: (_, newIndex) => setCurrentSlide(newIndex),
        nextArrow: <CustomNextArrow />,
        prevArrow: <CustomPrevArrow visibility={currentSlide === 0 ? 'hidden' : 'visible'} />,
        responsive: [
            {
                breakpoint: 767,
                settings: {
                    infinite: false,
                    arrows: true,
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    dots: true
                }
            }
        ]
    }

    const previousSearchParams = useMemo(() => {
        const {snitizeUrlSearchParam} = getUrlSearchParam(location.search)
        return snitizeUrlSearchParam
    }, [location.search])
    return (
        <Box sx={ProductsContainerStyles}>
            {slides.length > 0 && (
                <>
                    <Heading
                        as="h2"
                        textAlign="center"
                        m={{base: 2, md: 6}}
                        fontSize={{base: '20px', md: '32px'}}
                    >
                        {sliderTitle}
                    </Heading>
                    <Slider {...settings}>
                        {slides.map((slideHtml, index) => (
                            <Box
                                sx={ProductTileStyles}
                                key={index}
                                dangerouslySetInnerHTML={{__html: slideHtml}}
                            />
                        ))}
                    </Slider>
                </>
            )}
            {product?.id && (
                <QuickView
                    isOpen={isOpen}
                    onClose={onClose}
                    product={product}
                    previousSearchParams={previousSearchParams}
                />
            )}
        </Box>
    )
}

ProductsCollection.propTypes = {
    category: PropTypes.object
}

export default ProductsCollection
